// utils/doubleEntryService.js
import axios from "axios";

/**
 * Double-Entry Accounting Service
 *
 * This service implements standard double-entry bookkeeping where:
 * - Every transaction has at least one debit and one credit
 * - Total debits always equal total credits
 * - Follows the equation: Assets = Liabilities + Equity
 */

const API_BASE = "https://zedlenders.pickmesms.com/items";

// Chart of Accounts Structure
export const ACCOUNT_TYPES = {
  ASSET: "Asset",
  LIABILITY: "Liability",
  EQUITY: "Equity",
  REVENUE: "Revenue",
  EXPENSE: "Expense",
};

// Standard account categories
export const STANDARD_ACCOUNTS = {
  // Assets
  CASH: { code: "1000", name: "Cash", type: ACCOUNT_TYPES.ASSET },
  LOANS_RECEIVABLE: { code: "1200", name: "Loans Receivable", type: ACCOUNT_TYPES.ASSET },
  INTEREST_RECEIVABLE: { code: "1210", name: "Interest Receivable", type: ACCOUNT_TYPES.ASSET },
  FIXED_ASSETS: { code: "1500", name: "Fixed Assets", type: ACCOUNT_TYPES.ASSET },
  ACCUMULATED_DEPRECIATION: { code: "1510", name: "Accumulated Depreciation", type: ACCOUNT_TYPES.ASSET, isContra: true },

  // Liabilities
  CUSTOMER_DEPOSITS: { code: "2000", name: "Customer Deposits", type: ACCOUNT_TYPES.LIABILITY },
  INVESTOR_CAPITAL: { code: "2100", name: "Investor Capital", type: ACCOUNT_TYPES.LIABILITY },
  WITHHOLDING_TAX_PAYABLE: { code: "2200", name: "Withholding Tax Payable", type: ACCOUNT_TYPES.LIABILITY },
  INTEREST_PAYABLE: { code: "2300", name: "Interest Payable", type: ACCOUNT_TYPES.LIABILITY },

  // Equity
  RETAINED_EARNINGS: { code: "3000", name: "Retained Earnings", type: ACCOUNT_TYPES.EQUITY },
  OWNER_EQUITY: { code: "3100", name: "Owner's Equity", type: ACCOUNT_TYPES.EQUITY },

  // Revenue
  INTEREST_INCOME: { code: "4000", name: "Interest Income", type: ACCOUNT_TYPES.REVENUE },
  ADMIN_FEE_INCOME: { code: "4100", name: "Admin Fee Income", type: ACCOUNT_TYPES.REVENUE },
  GPS_FEE_INCOME: { code: "4200", name: "GPS Fee Income", type: ACCOUNT_TYPES.REVENUE },
  PENALTY_INCOME: { code: "4300", name: "Penalty Income", type: ACCOUNT_TYPES.REVENUE },

  // Expenses
  OPERATIONAL_EXPENSE: { code: "5000", name: "Operational Expenses", type: ACCOUNT_TYPES.EXPENSE },
  TRANSFER_FEE_EXPENSE: { code: "5100", name: "Transfer Fee Expenses", type: ACCOUNT_TYPES.EXPENSE },
  INVESTMENT_INTEREST_EXPENSE: { code: "5200", name: "Investment Interest Expense", type: ACCOUNT_TYPES.EXPENSE },
  DEPRECIATION_EXPENSE: { code: "5300", name: "Depreciation Expense", type: ACCOUNT_TYPES.EXPENSE },
};

export class DoubleEntryService {
  /**
   * Create a journal entry with debits and credits
   * @param {Object} params
   * @param {string} params.company - Company ID
   * @param {string} params.description - Entry description
   * @param {Date} params.date - Transaction date
   * @param {Array} params.lines - Array of {accountCode, debit, credit, description}
   * @param {string} params.reference - Reference number (loan ID, transaction ID, etc.)
   * @param {string} params.token - Auth token
   */
  static async createJournalEntry({ company, description, date, lines, reference, token }) {
    try {
      // Validate: Total debits must equal total credits
      const totalDebits = lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
      const totalCredits = lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);

      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error(`Journal entry is not balanced. Debits: ${totalDebits}, Credits: ${totalCredits}`);
      }

      // Create journal entry header
      const entryResponse = await axios.post(
        `${API_BASE}/journal_entries`,
        {
          company,
          entry_date: date instanceof Date ? date.toISOString().split('T')[0] : date,
          description,
          reference,
          total_debit: totalDebits,
          total_credit: totalCredits,
          status: "posted",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const entryId = entryResponse.data.data.id;

      // Create journal entry lines
      const linePromises = lines.map((line) =>
        axios.post(
          `${API_BASE}/journal_entry_lines`,
          {
            journal_entry: entryId,
            account_code: line.accountCode,
            account_name: line.accountName,
            debit_amount: parseFloat(line.debit) || 0,
            credit_amount: parseFloat(line.credit) || 0,
            description: line.description || description,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        )
      );

      await Promise.all(linePromises);

      return {
        success: true,
        entryId,
        message: "Journal entry created successfully",
      };
    } catch (error) {
      console.error("Error creating journal entry:", error);
      throw error;
    }
  }

  /**
   * Record loan disbursement in double-entry format
   */
  static async recordLoanDisbursement({ loanId, amount, date, company, token, accountId }) {
    const description = `Loan disbursement - Loan ID: ${loanId}`;

    return await this.createJournalEntry({
      company,
      description,
      date,
      reference: `LOAN-${loanId}`,
      lines: [
        {
          accountCode: STANDARD_ACCOUNTS.LOANS_RECEIVABLE.code,
          accountName: STANDARD_ACCOUNTS.LOANS_RECEIVABLE.name,
          debit: amount,
          credit: 0,
          description: "Loan disbursed to borrower",
        },
        {
          accountCode: STANDARD_ACCOUNTS.CASH.code,
          accountName: STANDARD_ACCOUNTS.CASH.name,
          debit: 0,
          credit: amount,
          description: `Cash paid from account ${accountId}`,
        },
      ],
      token,
    });
  }

  /**
   * Record loan repayment (principal + interest)
   */
  static async recordLoanRepayment({ loanId, principalAmount, interestAmount, date, company, token, accountId }) {
    const totalAmount = parseFloat(principalAmount) + parseFloat(interestAmount);
    const description = `Loan repayment - Loan ID: ${loanId}`;

    const lines = [
      {
        accountCode: STANDARD_ACCOUNTS.CASH.code,
        accountName: STANDARD_ACCOUNTS.CASH.name,
        debit: totalAmount,
        credit: 0,
        description: `Cash received to account ${accountId}`,
      },
    ];

    // Credit Loans Receivable for principal
    if (principalAmount > 0) {
      lines.push({
        accountCode: STANDARD_ACCOUNTS.LOANS_RECEIVABLE.code,
        accountName: STANDARD_ACCOUNTS.LOANS_RECEIVABLE.name,
        debit: 0,
        credit: principalAmount,
        description: "Principal repayment",
      });
    }

    // Credit Interest Income for interest
    if (interestAmount > 0) {
      lines.push({
        accountCode: STANDARD_ACCOUNTS.INTEREST_INCOME.code,
        accountName: STANDARD_ACCOUNTS.INTEREST_INCOME.name,
        debit: 0,
        credit: interestAmount,
        description: "Interest income earned",
      });
    }

    return await this.createJournalEntry({
      company,
      description,
      date,
      reference: `REPAY-${loanId}`,
      lines,
      token,
    });
  }

  /**
   * Record fee income (admin fees, GPS fees, penalties)
   */
  static async recordFeeIncome({ feeType, amount, date, company, token, loanId, accountId }) {
    let accountCode, accountName;

    switch (feeType) {
      case "Admin Fees":
        accountCode = STANDARD_ACCOUNTS.ADMIN_FEE_INCOME.code;
        accountName = STANDARD_ACCOUNTS.ADMIN_FEE_INCOME.name;
        break;
      case "GPS Fee Payment":
        accountCode = STANDARD_ACCOUNTS.GPS_FEE_INCOME.code;
        accountName = STANDARD_ACCOUNTS.GPS_FEE_INCOME.name;
        break;
      case "Penalty Fee":
        accountCode = STANDARD_ACCOUNTS.PENALTY_INCOME.code;
        accountName = STANDARD_ACCOUNTS.PENALTY_INCOME.name;
        break;
      default:
        accountCode = STANDARD_ACCOUNTS.ADMIN_FEE_INCOME.code;
        accountName = STANDARD_ACCOUNTS.ADMIN_FEE_INCOME.name;
    }

    return await this.createJournalEntry({
      company,
      description: `${feeType} - Loan ID: ${loanId}`,
      date,
      reference: `FEE-${loanId}`,
      lines: [
        {
          accountCode: STANDARD_ACCOUNTS.CASH.code,
          accountName: STANDARD_ACCOUNTS.CASH.name,
          debit: amount,
          credit: 0,
          description: `Cash received to account ${accountId}`,
        },
        {
          accountCode,
          accountName,
          debit: 0,
          credit: amount,
          description: `${feeType} income`,
        },
      ],
      token,
    });
  }

  /**
   * Record operational expenses
   */
  static async recordExpense({ amount, description, date, company, token, accountId }) {
    return await this.createJournalEntry({
      company,
      description: `Operational expense: ${description}`,
      date,
      reference: `EXP-${Date.now()}`,
      lines: [
        {
          accountCode: STANDARD_ACCOUNTS.OPERATIONAL_EXPENSE.code,
          accountName: STANDARD_ACCOUNTS.OPERATIONAL_EXPENSE.name,
          debit: amount,
          credit: 0,
          description,
        },
        {
          accountCode: STANDARD_ACCOUNTS.CASH.code,
          accountName: STANDARD_ACCOUNTS.CASH.name,
          debit: 0,
          credit: amount,
          description: `Cash paid from account ${accountId}`,
        },
      ],
      token,
    });
  }

  /**
   * Record float addition (investment/capital injection)
   */
  static async recordFloatAddition({ amount, date, company, token, accountId, source = "Owner" }) {
    return await this.createJournalEntry({
      company,
      description: `Float addition from ${source}`,
      date,
      reference: `FLOAT-${Date.now()}`,
      lines: [
        {
          accountCode: STANDARD_ACCOUNTS.CASH.code,
          accountName: STANDARD_ACCOUNTS.CASH.name,
          debit: amount,
          credit: 0,
          description: `Cash added to account ${accountId}`,
        },
        {
          accountCode: source === "Investor" ? STANDARD_ACCOUNTS.INVESTOR_CAPITAL.code : STANDARD_ACCOUNTS.OWNER_EQUITY.code,
          accountName: source === "Investor" ? STANDARD_ACCOUNTS.INVESTOR_CAPITAL.name : STANDARD_ACCOUNTS.OWNER_EQUITY.name,
          debit: 0,
          credit: amount,
          description: `Capital contribution from ${source}`,
        },
      ],
      token,
    });
  }

  /**
   * Record investment deposit (investor funds in)
   */
  static async recordInvestmentDeposit({
    investmentId,
    amount,
    date,
    company,
    token,
    accountId,
  }) {
    return await this.createJournalEntry({
      company,
      description: `Investment deposit - Investment ID: ${investmentId}`,
      date,
      reference: `INV-DEP-${investmentId}`,
      lines: [
        {
          accountCode: STANDARD_ACCOUNTS.CASH.code,
          accountName: STANDARD_ACCOUNTS.CASH.name,
          debit: amount,
          credit: 0,
          description: `Cash received to account ${accountId}`,
        },
        {
          accountCode: STANDARD_ACCOUNTS.INVESTOR_CAPITAL.code,
          accountName: STANDARD_ACCOUNTS.INVESTOR_CAPITAL.name,
          debit: 0,
          credit: amount,
          description: "Investor capital contribution",
        },
      ],
      token,
    });
  }

  /**
   * Record investment payout (principal + interest, with withholding tax)
   */
  static async recordInvestmentPayout({
    investmentId,
    principalAmount,
    interestAmount,
    withholdingTaxAmount = 0,
    date,
    company,
    token,
    accountId,
  }) {
    const totalCashOut = Number(principalAmount || 0) + Number(interestAmount || 0) - Number(withholdingTaxAmount || 0);
    const description = `Investment payout - Investment ID: ${investmentId}`;

    const lines = [];

    if (Number(principalAmount || 0) > 0) {
      lines.push({
        accountCode: STANDARD_ACCOUNTS.INVESTOR_CAPITAL.code,
        accountName: STANDARD_ACCOUNTS.INVESTOR_CAPITAL.name,
        debit: principalAmount,
        credit: 0,
        description: "Return of principal",
      });
    }

    if (Number(interestAmount || 0) > 0) {
      lines.push({
        accountCode: STANDARD_ACCOUNTS.INVESTMENT_INTEREST_EXPENSE.code,
        accountName: STANDARD_ACCOUNTS.INVESTMENT_INTEREST_EXPENSE.name,
        debit: interestAmount,
        credit: 0,
        description: "Investment interest expense",
      });
    }

    if (Number(withholdingTaxAmount || 0) > 0) {
      lines.push({
        accountCode: STANDARD_ACCOUNTS.WITHHOLDING_TAX_PAYABLE.code,
        accountName: STANDARD_ACCOUNTS.WITHHOLDING_TAX_PAYABLE.name,
        debit: 0,
        credit: withholdingTaxAmount,
        description: "Withholding tax on interest",
      });
    }

    if (totalCashOut > 0) {
      lines.push({
        accountCode: STANDARD_ACCOUNTS.CASH.code,
        accountName: STANDARD_ACCOUNTS.CASH.name,
        debit: 0,
        credit: totalCashOut,
        description: `Cash paid from account ${accountId}`,
      });
    }

    return await this.createJournalEntry({
      company,
      description,
      date,
      reference: `INV-PAY-${investmentId}`,
      lines,
      token,
    });
  }

  /**
   * Get trial balance (sum of all debits and credits per account)
   */
  static async getTrialBalance({ company, token, startDate = null, endDate = null }) {
    try {
      const params = {
        filter: { company },
        fields: "*,journal_entry_lines.*",
        limit: -1,
      };

      if (startDate && endDate) {
        params.filter.entry_date = {
          _between: [startDate, endDate],
        };
      }

      const response = await axios.get(`${API_BASE}/journal_entries`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const entries = response.data.data || [];
      const accountBalances = {};

      // Sum up all debits and credits per account
      entries.forEach((entry) => {
        if (entry.journal_entry_lines) {
          entry.journal_entry_lines.forEach((line) => {
            const code = line.account_code;
            if (!accountBalances[code]) {
              accountBalances[code] = {
                code,
                name: line.account_name,
                debit: 0,
                credit: 0,
                balance: 0,
              };
            }
            accountBalances[code].debit += parseFloat(line.debit_amount) || 0;
            accountBalances[code].credit += parseFloat(line.credit_amount) || 0;
          });
        }
      });

      // Calculate balances
      Object.values(accountBalances).forEach((account) => {
        account.balance = account.debit - account.credit;
      });

      return {
        accounts: Object.values(accountBalances),
        totalDebits: Object.values(accountBalances).reduce((sum, acc) => sum + acc.debit, 0),
        totalCredits: Object.values(accountBalances).reduce((sum, acc) => sum + acc.credit, 0),
      };
    } catch (error) {
      console.error("Error getting trial balance:", error);
      throw error;
    }
  }

  /**
   * Get general ledger for a specific account
   */
  static async getGeneralLedger({ company, token, accountCode, startDate = null, endDate = null }) {
    try {
      const params = {
        filter: { company },
        fields: "*,journal_entry_lines.*",
        limit: -1,
        sort: "entry_date",
      };

      if (startDate && endDate) {
        params.filter.entry_date = {
          _between: [startDate, endDate],
        };
      }

      const response = await axios.get(`${API_BASE}/journal_entries`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const entries = response.data.data || [];
      const ledgerEntries = [];
      let runningBalance = 0;

      entries.forEach((entry) => {
        if (entry.journal_entry_lines) {
          const relevantLines = entry.journal_entry_lines.filter(
            (line) => line.account_code === accountCode
          );

          relevantLines.forEach((line) => {
            const debit = parseFloat(line.debit_amount) || 0;
            const credit = parseFloat(line.credit_amount) || 0;
            runningBalance += debit - credit;

            ledgerEntries.push({
              date: entry.entry_date,
              reference: entry.reference,
              description: line.description,
              debit,
              credit,
              balance: runningBalance,
            });
          });
        }
      });

      return ledgerEntries;
    } catch (error) {
      console.error("Error getting general ledger:", error);
      throw error;
    }
  }

  /**
   * Get balance sheet data
   */
  static async getBalanceSheet({ company, token, asOfDate = null }) {
    try {
      const trialBalance = await this.getTrialBalance({
        company,
        token,
        startDate: null,
        endDate: asOfDate,
      });

      const balanceSheet = {
        assets: {},
        liabilities: {},
        equity: {},
        totalAssets: 0,
        totalLiabilities: 0,
        totalEquity: 0,
      };

      trialBalance.accounts.forEach((account) => {
        const accountType = Object.values(STANDARD_ACCOUNTS).find(
          (acc) => acc.code === account.code
        )?.type;

        if (accountType === ACCOUNT_TYPES.ASSET) {
          balanceSheet.assets[account.code] = account;
          balanceSheet.totalAssets += account.balance;
        } else if (accountType === ACCOUNT_TYPES.LIABILITY) {
          balanceSheet.liabilities[account.code] = account;
          balanceSheet.totalLiabilities += Math.abs(account.balance);
        } else if (accountType === ACCOUNT_TYPES.EQUITY) {
          balanceSheet.equity[account.code] = account;
          balanceSheet.totalEquity += Math.abs(account.balance);
        } else if (accountType === ACCOUNT_TYPES.REVENUE) {
          // Revenue increases equity (credit balance, shown as negative in balance)
          // Add absolute value to equity
          balanceSheet.totalEquity += Math.abs(account.balance);
        } else if (accountType === ACCOUNT_TYPES.EXPENSE) {
          // Expenses decrease equity (debit balance, shown as positive in balance)
          // Subtract from equity
          balanceSheet.totalEquity -= Math.abs(account.balance);
        }
      });

      // Fetch fixed assets from assets table
      try {
        const moment = require("moment");
        const assetsResponse = await axios.get(`${API_BASE}/assets`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            filter: {
              company: { _eq: company },
              status: { _in: ["active", "maintenance"] }, // Only active assets on balance sheet
            },
            fields: "*",
          },
        });

        const assets = assetsResponse.data.data || [];
        let totalFixedAssetsCost = 0;
        let totalAccumulatedDepreciation = 0;

        const endDate = asOfDate ? moment(asOfDate) : moment();

        assets.forEach((asset) => {
          totalFixedAssetsCost += parseFloat(asset.purchase_price || 0);

          // Calculate depreciation up to asOfDate
          if (asset.depreciation_method && asset.depreciation_method !== "none") {
            const purchaseDate = moment(asset.purchase_date);
            const yearsElapsed = endDate.diff(purchaseDate, "years", true);
            const annualDepreciationRate = parseFloat(asset.depreciation_rate || 0);
            const purchasePrice = parseFloat(asset.purchase_price || 0);
            const salvageValue = parseFloat(asset.salvage_value || 0);

            let depreciation = 0;

            if (asset.depreciation_method === "straight_line") {
              const annualDepreciation = (purchasePrice * annualDepreciationRate) / 100;
              depreciation = Math.min(annualDepreciation * yearsElapsed, purchasePrice - salvageValue);
            } else if (asset.depreciation_method === "declining_balance") {
              const currentValue = purchasePrice * Math.pow(1 - annualDepreciationRate / 100, yearsElapsed);
              depreciation = Math.max(purchasePrice - Math.max(currentValue, salvageValue), 0);
            }

            totalAccumulatedDepreciation += depreciation;
          }
        });

        // Add Fixed Assets (at cost) to balance sheet
        if (totalFixedAssetsCost > 0) {
          balanceSheet.assets["1500"] = {
            code: "1500",
            name: "Fixed Assets (at cost)",
            balance: totalFixedAssetsCost,
          };
          balanceSheet.totalAssets += totalFixedAssetsCost;
        }

        // Add Accumulated Depreciation (contra-asset, reduces total assets)
        if (totalAccumulatedDepreciation > 0) {
          balanceSheet.assets["1510"] = {
            code: "1510",
            name: "Less: Accumulated Depreciation",
            balance: -totalAccumulatedDepreciation, // Negative because it reduces assets
          };
          balanceSheet.totalAssets -= totalAccumulatedDepreciation;
        }

        // Add net book value line for clarity
        if (totalFixedAssetsCost > 0) {
          balanceSheet.assets["1520"] = {
            code: "1520",
            name: "Net Fixed Assets",
            balance: totalFixedAssetsCost - totalAccumulatedDepreciation,
            isCalculated: true,
          };
          // Don't add to totalAssets again - already counted above
        }
      } catch (assetsError) {
        console.warn("Could not fetch fixed assets for balance sheet:", assetsError);
        // Continue without fixed assets if there's an error
      }

      return balanceSheet;
    } catch (error) {
      console.error("Error getting balance sheet:", error);
      throw error;
    }
  }

  /**
   * Get income statement (Profit & Loss) data
   */
  static async getIncomeStatement({ company, token, startDate, endDate }) {
    try {
      const trialBalance = await this.getTrialBalance({
        company,
        token,
        startDate,
        endDate,
      });

      const incomeStatement = {
        revenue: {},
        expenses: {},
        totalRevenue: 0,
        totalExpenses: 0,
      };

      trialBalance.accounts.forEach((account) => {
        const accountType = Object.values(STANDARD_ACCOUNTS).find(
          (acc) => acc.code === account.code
        )?.type;

        if (accountType === ACCOUNT_TYPES.REVENUE) {
          // Revenue accounts have credit balances (negative in our calculation)
          incomeStatement.revenue[account.code] = account;
          incomeStatement.totalRevenue += Math.abs(account.balance);
        } else if (accountType === ACCOUNT_TYPES.EXPENSE) {
          // Expense accounts have debit balances (positive in our calculation)
          incomeStatement.expenses[account.code] = account;
          incomeStatement.totalExpenses += Math.abs(account.balance);
        }
      });

      return incomeStatement;
    } catch (error) {
      console.error("Error getting income statement:", error);
      throw error;
    }
  }
}

export default DoubleEntryService;
